import type { UglaFile } from '../types/ugla-types';

/**
 * Error thrown when UGLA file loading fails
 */
export class UglaLoadError extends Error {
  constructor(message: string, public cause?: unknown) {
    super(message);
    this.name = 'UglaLoadError';
  }
}

/**
 * Loads a UGLA file from a URL
 * @param url - URL to the UGLA file (JSON)
 * @returns Promise that resolves to the UGLA file content as a string
 * @throws {UglaLoadError} If the file cannot be loaded
 */
export const loadUglaFileFromUrl = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new UglaLoadError(
        `Failed to load UGLA file from ${url}: ${response.status} ${response.statusText}`
      );
    }

    const content = await response.text();
    return content;
  } catch (error) {
    if (error instanceof UglaLoadError) {
      throw error;
    }
    throw new UglaLoadError(
      `Failed to load UGLA file from ${url}`,
      error
    );
  }
};

/**
 * Loads a UGLA file from a local File object
 * @param file - File object from file input
 * @returns Promise that resolves to the UGLA file content as a string
 * @throws {UglaLoadError} If the file cannot be loaded
 */
export const loadUglaFileFromFile = async (file: File): Promise<string> => {
  try {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        if (event.target?.result && typeof event.target.result === 'string') {
          resolve(event.target.result);
        } else {
          reject(
            new UglaLoadError('Failed to read file: result is not a string')
          );
        }
      };

      reader.onerror = () => {
        reject(new UglaLoadError('Failed to read file: FileReader error'));
      };

      reader.readAsText(file);
    });
  } catch (error) {
    if (error instanceof UglaLoadError) {
      throw error;
    }
    throw new UglaLoadError('Failed to load UGLA file from File object', error);
  }
};

/**
 * Validates that a string is valid JSON and has the basic structure of a UGLA file
 * @param content - String content to validate
 * @returns True if the content appears to be a valid UGLA file
 */
export const validateUglaFile = (content: string): boolean => {
  try {
    const parsed = JSON.parse(content);

    // Check for basic UGLA file structure
    if (typeof parsed !== 'object' || parsed === null) {
      console.error('Failed to validate UGLA file: parsed is not an object');
      return false;
    }

    // UGLA files should have at least an assets array
    if (!Array.isArray(parsed.assets)) {
      console.error('Failed to validate UGLA file: assets is not an array');
      return false;
    }

    return true;
  } catch(error) {
    console.error('Failed to validate UGLA file', error);
    return false;
  }
};

/**
 * Parses a UGLA file string into a UglaFile object
 * @param content - UGLA file content as a string
 * @returns Parsed UGLA file object
 * @throws {UglaLoadError} If the content is not valid JSON or UGLA format
 */
export const parseUglaFile = (content: string): UglaFile => {
  if (!validateUglaFile(content)) {
    throw new UglaLoadError('Invalid UGLA file format');
  }

  try {
    return JSON.parse(content) as UglaFile;
  } catch (error) {
    throw new UglaLoadError('Failed to parse UGLA file JSON', error);
  }
};

/**
 * Loads and parses a UGLA file from a URL
 * @param url - URL to the UGLA file
 * @returns Promise that resolves to the parsed UGLA file
 * @throws {UglaLoadError} If the file cannot be loaded or parsed
 */
export const loadAndParseUglaFileFromUrl = async (
  url: string
): Promise<UglaFile> => {
  const content = await loadUglaFileFromUrl(url);
  return parseUglaFile(content);
};

/**
 * Loads and parses a UGLA file from a local File object
 * @param file - File object from file input
 * @returns Promise that resolves to the parsed UGLA file
 * @throws {UglaLoadError} If the file cannot be loaded or parsed
 */
export const loadAndParseUglaFileFromFile = async (
  file: File
): Promise<UglaFile> => {
  const content = await loadUglaFileFromFile(file);
  return parseUglaFile(content);
};
